/**
 * Location Service - Manages location switching and location-specific data
 * Handles location transitions, NPC loading, tilemap changes, and lighting updates
 */

import { railwayWorldStateService } from './railwayWorldStateService';
import { tilemapService } from './tilemapService';

export interface LocationData {
  id: string;
  name: string;
  tileset: string;
  spawnPoint: { x: number; y: number };
  lightSources?: Array<{
    x: number;
    y: number;
    radius: number;
    intensity: number;
    color: number;
  }>;
  timeOfDay?: 'dawn' | 'day' | 'dusk' | 'night';
  ambientLight?: number;
}

export interface LocationTransition {
  fromLocation: string;
  toLocation: string;
  playerPosition: { x: number; y: number };
}

class LocationService {
  private currentLocation: string = 'neo-tokyo-plaza';
  private locations: Map<string, LocationData> = new Map();
  private transitionCallbacks: Array<(transition: LocationTransition) => void> = [];

  constructor() {
    this.initializeLocations();
  }

  /**
   * Initialize location data
   */
  private initializeLocations(): void {
    const locations: LocationData[] = [
      {
        id: 'neo-tokyo-plaza',
        name: 'Neo-Tokyo Central Plaza',
        tileset: 'neo-tokyo',
        spawnPoint: { x: 400, y: 300 },
        lightSources: [
          { x: 200, y: 200, radius: 200, intensity: 0.4, color: 0xffaa00 },
          { x: 600, y: 400, radius: 200, intensity: 0.4, color: 0xffaa00 },
          { x: 1000, y: 300, radius: 200, intensity: 0.4, color: 0xffaa00 },
          { x: 800, y: 700, radius: 200, intensity: 0.4, color: 0xffaa00 },
          { x: 1200, y: 800, radius: 200, intensity: 0.4, color: 0xffaa00 },
        ],
        timeOfDay: 'day',
        ambientLight: 0.5,
      },
      {
        id: 'underground-hub',
        name: 'Underground Hacker Den',
        tileset: 'base',
        spawnPoint: { x: 400, y: 300 },
        lightSources: [
          { x: 300, y: 250, radius: 150, intensity: 0.3, color: 0x00ff00 },
          { x: 700, y: 450, radius: 150, intensity: 0.3, color: 0x00ff00 },
        ],
        timeOfDay: 'night',
        ambientLight: 0.2,
      },
      {
        id: 'corporate-district',
        name: 'Corporate District Servers',
        tileset: 'base',
        spawnPoint: { x: 400, y: 300 },
        lightSources: [
          { x: 400, y: 300, radius: 300, intensity: 0.5, color: 0xffffff },
        ],
        timeOfDay: 'day',
        ambientLight: 0.7,
      },
      {
        id: 'digital-realm',
        name: 'Digital Market Exchange',
        tileset: 'base',
        spawnPoint: { x: 400, y: 300 },
        lightSources: [],
        timeOfDay: 'day',
        ambientLight: 0.8,
      },
      {
        id: 'neutral-zone',
        name: 'The Neutral Zone',
        tileset: 'base',
        spawnPoint: { x: 400, y: 300 },
        lightSources: [
          { x: 400, y: 300, radius: 250, intensity: 0.4, color: 0x8888ff },
        ],
        timeOfDay: 'dusk',
        ambientLight: 0.4,
      },
    ];

    for (const location of locations) {
      this.locations.set(location.id, location);
    }

    console.log('[LocationService] Initialized', this.locations.size, 'locations');
  }

  /**
   * Get current location ID
   */
  getCurrentLocation(): string {
    return this.currentLocation;
  }

  /**
   * Get location data
   */
  getLocationData(locationId: string): LocationData | null {
    return this.locations.get(locationId) || null;
  }

  /**
   * Get all available locations
   */
  getAllLocations(): LocationData[] {
    return Array.from(this.locations.values());
  }

  /**
   * Switch to a new location
   */
  async switchLocation(
    newLocationId: string,
    playerPosition?: { x: number; y: number }
  ): Promise<LocationTransition | null> {
    if (!this.locations.has(newLocationId)) {
      console.error(`[LocationService] Location ${newLocationId} not found`);
      return null;
    }

    const fromLocation = this.currentLocation;
    const locationData = this.locations.get(newLocationId)!;

    // Create transition
    const transition: LocationTransition = {
      fromLocation,
      toLocation: newLocationId,
      playerPosition: playerPosition || locationData.spawnPoint,
    };

    // Update current location
    this.currentLocation = newLocationId;

    // Notify callbacks
    for (const callback of this.transitionCallbacks) {
      callback(transition);
    }

    console.log(`[LocationService] Switched from ${fromLocation} to ${newLocationId}`);

    return transition;
  }

  /**
   * Register callback for location transitions
   */
  onLocationTransition(callback: (transition: LocationTransition) => void): () => void {
    this.transitionCallbacks.push(callback);
    return () => {
      const index = this.transitionCallbacks.indexOf(callback);
      if (index > -1) {
        this.transitionCallbacks.splice(index, 1);
      }
    };
  }

  /**
   * Check if location exists
   */
  locationExists(locationId: string): boolean {
    return this.locations.has(locationId);
  }

  /**
   * Get spawn point for location
   */
  getSpawnPoint(locationId: string): { x: number; y: number } {
    const location = this.locations.get(locationId);
    return location?.spawnPoint || { x: 400, y: 300 };
  }
}

export const locationService = new LocationService();

